import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllUsersWithRoles, syncUserRoles } from '../../../redux/Actions/adminActions';
import {
    Container, Typography, Box, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Button, Chip, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, OutlinedInput,
    Snackbar, Alert, Checkbox, TextField
} from "@mui/material";
import { Edit } from '@mui/icons-material';
import AdminNavbar from './AdminNavbar';
import api from "lib/api/api";

const ALL_ROLES = ['Admin', 'Student', 'Teacher', 'CONSULTANT', 'AUSBILDUNG_MANAGER', 'TEACHER_MANAGER', 'ADMIN_DEVELOPER'];

const ManageAllUsers = () => {
    const dispatch = useDispatch();
    const { users } = useSelector(state => state.admin);
    const [selectedUser, setSelectedUser] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [userRoles, setUserRoles] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [emailUser, setEmailUser] = useState(null);
    const [emailSubject, setEmailSubject] = useState("");
    const [emailMessage, setEmailMessage] = useState("");
    const [emailSending, setEmailSending] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const filteredUsers = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return users;

        return users.filter((user) => {
            const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
            return [fullName, user.firstName, user.lastName, user.email]
                .some((value) => String(value || "").toLowerCase().includes(query));
        });
    }, [searchQuery, users]);

    useEffect(() => {
        dispatch(fetchAllUsersWithRoles());
    }, [dispatch]);

    const handleEditRoles = (user) => {
        setSelectedUser(user);
        setUserRoles(user.roles || []);
        setOpenModal(true);
    };

    const handleSave = async () => {
        try {
            await dispatch(syncUserRoles(selectedUser.id, userRoles));
            setSnackbar({ open: true, message: "User roles synced successfully!", severity: "success" });
            setOpenModal(false);
        } catch (err) {
            setSnackbar({ open: true, message: "Error updating roles: " + (err.message || "Unknown error"), severity: "error" });
        }
    };

    const forceLogoutAllUsers = async () => {
        if (!window.confirm("Force logout all users? Everyone will need to login again.")) return;
        try {
            const response = await api.post("/admin-developer/security/force-logout-all");
            setSnackbar({
                open: true,
                message: response.data?.message || "All user sessions were invalidated.",
                severity: "success"
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.message || "Could not force logout all users.",
                severity: "error"
            });
        }
    };

    const forceLogoutUser = async (user) => {
        if (!window.confirm(`Force logout ${user.email}?`)) return;
        try {
            const response = await api.post(`/admin-developer/security/users/${user.id}/force-logout`);
            setSnackbar({
                open: true,
                message: response.data?.message || "User sessions were invalidated.",
                severity: "success"
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.message || "Could not force logout user.",
                severity: "error"
            });
        }
    };

    const toggleUserSelection = (userId) => {
        setSelectedUserIds((prev) =>
            prev.includes(userId)
                ? prev.filter((id) => id !== userId)
                : [...prev, userId]
        );
    };

    const toggleAllUsers = () => {
        const ids = filteredUsers.map((user) => user.id);
        const allSelected = ids.length > 0 && ids.every((id) => selectedUserIds.includes(id));
        setSelectedUserIds((prev) =>
            allSelected
                ? prev.filter((id) => !ids.includes(id))
                : Array.from(new Set([...prev, ...ids]))
        );
    };

    const deleteUser = async (user) => {
        if (!window.confirm(`Delete user ${user.email}? Users with linked records will be blocked.`)) return;
        try {
            await api.delete(`/admin-developer/users/${user.id}`);
            setSelectedUserIds((prev) => prev.filter((id) => id !== user.id));
            dispatch(fetchAllUsersWithRoles());
            setSnackbar({ open: true, message: "User deleted.", severity: "success" });
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.message || "Could not delete user.",
                severity: "error"
            });
        }
    };

    const deleteSelectedUsers = async () => {
        if (!selectedUserIds.length) return;
        if (!window.confirm(`Delete ${selectedUserIds.length} selected user(s)? Admins and users with linked records will be blocked.`)) return;
        try {
            await api.post("/admin-developer/users/delete-bulk", selectedUserIds);
            setSelectedUserIds([]);
            dispatch(fetchAllUsersWithRoles());
            setSnackbar({ open: true, message: "Selected users deleted.", severity: "success" });
        } catch (err) {
            dispatch(fetchAllUsersWithRoles());
            setSnackbar({
                open: true,
                message: err.response?.data?.message || "Some users could not be deleted.",
                severity: "error"
            });
        }
    };

    const sendUserEmail = async () => {
        if (!emailSubject.trim() || !emailMessage.trim()) return;
        setEmailSending(true);
        try {
            const response = await api.post(`/admin-developer/users/${emailUser.id}/send-email`, {
                subject: emailSubject.trim(),
                message: emailMessage.trim()
            });
            setSnackbar({ open: true, message: response.data?.message || "Email sent.", severity: "success" });
            setEmailUser(null);
            setEmailSubject("");
            setEmailMessage("");
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || "Could not send email.", severity: "error" });
        } finally {
            setEmailSending(false);
        }
    };

    const sendPasswordReset = async (user) => {
        if (!window.confirm(`Send a password reset email to ${user.email}?`)) return;
        try {
            const response = await api.post(`/admin-developer/users/${user.id}/send-password-reset`);
            setSnackbar({ open: true, message: response.data?.message || "Password reset email sent.", severity: "success" });
        } catch (err) {
            setSnackbar({ open: true, message: err.response?.data?.message || "Could not send password reset email.", severity: "error" });
        }
    };

    return (
        <div className="admin-dashboard-layout">
            <AdminNavbar />
            <Container sx={{ mt: 4 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center", mb: 2 }}>
                    <Typography variant="h4" fontWeight={800}>
                        Manage All Users
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <Button
                            color="error"
                            variant="outlined"
                            disabled={!selectedUserIds.length}
                            onClick={deleteSelectedUsers}
                        >
                            Delete selected ({selectedUserIds.length})
                        </Button>
                        <Button color="error" variant="contained" onClick={forceLogoutAllUsers}>
                            Force logout all users
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
                    <TextField
                        label="Search users"
                        placeholder="Name or email"
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        size="small"
                        sx={{ minWidth: { xs: "100%", sm: 320 } }}
                    />
                    <Typography variant="body2" color="text.secondary">
                        {filteredUsers.length} of {users.length} users
                    </Typography>
                </Box>

                <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        size="small"
                                        checked={filteredUsers.length > 0 && filteredUsers.every((user) => selectedUserIds.includes(user.id))}
                                        indeterminate={filteredUsers.some((user) => selectedUserIds.includes(user.id)) && !filteredUsers.every((user) => selectedUserIds.includes(user.id))}
                                        onChange={toggleAllUsers}
                                    />
                                </TableCell>
                                <TableCell><strong>Name</strong></TableCell>
                                <TableCell><strong>Email</strong></TableCell>
                                <TableCell><strong>Roles</strong></TableCell>
                                <TableCell align="center"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredUsers.map((user) => (
                                <TableRow key={user.id} hover selected={selectedUserIds.includes(user.id)}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            size="small"
                                            checked={selectedUserIds.includes(user.id)}
                                            onChange={() => toggleUserSelection(user.id)}
                                        />
                                    </TableCell>
                                    <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                                    <TableCell>{user.email}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {user.roles?.map((role) => (
                                                <Chip key={role} label={role} size="small" variant="outlined" />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton color="primary" onClick={() => handleEditRoles(user)}>
                                            <Edit />
                                        </IconButton>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => setEmailUser(user)}
                                            sx={{ ml: 1 }}
                                        >
                                            Send email
                                        </Button>
                                        <Button
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                            onClick={() => sendPasswordReset(user)}
                                            sx={{ ml: 1 }}
                                        >
                                            Reset password
                                        </Button>
                                        <Button
                                            size="small"
                                            color="warning"
                                            variant="outlined"
                                            onClick={() => forceLogoutUser(user)}
                                            sx={{ ml: 1 }}
                                        >
                                            Force logout
                                        </Button>
                                        <Button
                                            size="small"
                                            color="error"
                                            variant="outlined"
                                            onClick={() => deleteUser(user)}
                                            sx={{ ml: 1 }}
                                        >
                                            Delete
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredUsers.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                        No users match your search.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Container>

            <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Manage User Roles</DialogTitle>
                <DialogContent>
                    {selectedUser && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Editing roles for: <strong>{selectedUser.firstName} {selectedUser.lastName}</strong>
                            </Typography>
                            <FormControl fullWidth>
                                <InputLabel>Roles</InputLabel>
                                <Select
                                    multiple
                                    value={userRoles}
                                    onChange={(e) => setUserRoles(e.target.value)}
                                    input={<OutlinedInput label="Roles" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip key={value} label={value} />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {ALL_ROLES.map((name) => (
                                        <MenuItem key={name} value={name}>
                                            {name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenModal(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>Save Changes</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(emailUser)} onClose={() => !emailSending && setEmailUser(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Send Email</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
                        To: <strong>{emailUser?.email}</strong>
                    </Typography>
                    <TextField
                        label="Subject"
                        value={emailSubject}
                        onChange={(event) => setEmailSubject(event.target.value)}
                        disabled={emailSending}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Message"
                        value={emailMessage}
                        onChange={(event) => setEmailMessage(event.target.value)}
                        disabled={emailSending}
                        multiline
                        minRows={6}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setEmailUser(null)} disabled={emailSending}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={sendUserEmail}
                        disabled={emailSending || !emailSubject.trim() || !emailMessage.trim()}
                    >
                        {emailSending ? "Sending..." : "Send email"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 3 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default ManageAllUsers;
